import { IValidationFunction } from "./interface";
import { allowedNameCharactersPattern } from "./stripDisallowedNameCharacters";

/**
 * Validates a name for uniqueness and format.
 *
 * Checks in order:
 * 1. Blank/whitespace-only names are rejected.
 * 2. Duplicate names (case-insensitive, trimmed) are rejected using `target.options.existingValues`.
 * 3. Names containing characters outside alphanumerics, hyphens, underscores, and spaces are rejected.
 * 4. An optional `target.options.otherValues.warningText` is returned as a warning when the name otherwise validates.
 *
 * @param _ - Unused back-end URL parameter required by the shared validation function signature.
 * @param target - Validation target containing the candidate name, existing values and optional contextual warning.
 * @returns A resolved promise with a valid or invalid result for the candidate name.
 */
export const checkName: IValidationFunction = async (_, target) => {
  let valid = false;
  let validationText: string = "Not validated";
  if (target.value.trim() === "") {
    valid = false;
    validationText = "Name cannot be blank";
  } else if (
    (target.options?.existingValues ?? []).findIndex(
      (s) => s.trim().toLowerCase() === target.value.trim().toLowerCase(),
    ) >= 0
  ) {
    valid = false;
    validationText = "This name is taken";
  } else if (target.value.match(allowedNameCharactersPattern) === null) {
    valid = false;
    validationText = "Only alphanumeric characters and -_ can be used";
  } else {
    valid = true;
    validationText = "Successfully validated";
  }
  const warningText = target.options?.otherValues?.warningText;
  if (valid && warningText !== undefined) {
    return {
      success: true,
      data: { valid: false, warning: true, validationText: warningText },
    };
  }
  return { success: true, data: { valid, validationText } };
};
